'use client'

import { useCallback, useEffect, useState, type CSSProperties } from 'react'

/* Public volunteer application form. Cascading geography (state → district → city) is populated
   from REAL masjid data via /api/volunteer/geo-options, so an applicant only picks a place where
   masjids exist. The applicant may stop at any level; the deepest filled level is their requested
   scope. Honest throughout: a DB error shows a real error, and success shows only the neutral
   "received — we'll review" message (no fabricated status or ETA). */

const label: CSSProperties = { fontSize: 13, fontWeight: 700, color: '#0B132B', marginBottom: 6, display: 'block' }
const field: CSSProperties = {
  width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid #dfe3ea',
  fontSize: 14.5, fontFamily: 'inherit', background: 'white', color: '#0B132B', boxSizing: 'border-box',
}
const row: CSSProperties = { marginBottom: 16 }

export default function VolunteerApplyForm() {
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('') // honeypot — kept hidden; a real user never fills it
  const [motivation, setMotivation] = useState('')

  const [state, setState] = useState('')
  const [district, setDistrict] = useState('')
  const [city, setCity] = useState('')
  const [states, setStates] = useState<string[]>([])
  const [districts, setDistricts] = useState<string[]>([])
  const [cities, setCities] = useState<string[]>([])

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const loadOptions = useCallback(async (level: 'state' | 'district' | 'city', parent?: string): Promise<string[]> => {
    try {
      const qs = new URLSearchParams({ level })
      if (parent) qs.set('parent', parent)
      const r = await fetch(`/api/volunteer/geo-options?${qs}`, { cache: 'no-store' })
      if (!r.ok) return []
      const d = await r.json()
      return Array.isArray(d.options) ? d.options : []
    } catch {
      return []
    }
  }, [])

  // States on mount.
  useEffect(() => { loadOptions('state').then(setStates) }, [loadOptions])

  // Districts when state changes (and reset the levels below).
  useEffect(() => {
    setDistrict(''); setCity(''); setDistricts([]); setCities([])
    if (state) loadOptions('district', state).then(setDistricts)
  }, [state, loadOptions])

  // Cities when district changes (and reset city).
  useEffect(() => {
    setCity(''); setCities([])
    if (district) loadOptions('city', district).then(setCities)
  }, [district, loadOptions])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (name.trim().length < 2) { setError('Please enter your name.'); return }
    if (!/^\+?[\d -]{8,20}$/.test(mobile.trim())) { setError('Please enter a valid mobile number.'); return }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setError('That email address doesn’t look right.'); return }
    if (!state) { setError('Please choose at least a state where you’d like to help.'); return }

    setSubmitting(true)
    try {
      const r = await fetch('/api/volunteer/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), mobile: mobile.trim(), email: email.trim(),
          scopeState: state, scopeDistrict: district, scopeCity: city,
          motivation: motivation.trim(), website,
        }),
      })
      const d = await r.json().catch(() => ({}))
      if (r.ok && d.ok) { setDone(true); return }
      setError(d.error || 'Couldn’t submit right now. Please try again later.')
    } catch {
      setError('Couldn’t submit right now. Please try again later.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14, padding: '22px 20px' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#166534' }}>Application received</div>
        <p style={{ fontSize: 14.5, color: '#3f6f4e', lineHeight: 1.6, marginTop: 8, marginBottom: 0 }}>
          Our team will review and get back to you. There’s nothing more to do for now.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} style={{ background: 'white', border: '1px solid #ececec', borderRadius: 16, padding: '22px 20px' }}>
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 14px', fontSize: 13.5, marginBottom: 16 }}>{error}</div>
      )}

      <div style={row}>
        <label style={label} htmlFor="v-name">Full name</label>
        <input id="v-name" style={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name" required />
      </div>

      <div style={row}>
        <label style={label} htmlFor="v-mobile">Mobile number</label>
        <input id="v-mobile" style={field} value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 98xxxxxxxx" inputMode="tel" autoComplete="tel" required />
      </div>

      <div style={row}>
        <label style={label} htmlFor="v-email">Email <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span></label>
        <input id="v-email" style={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" type="email" autoComplete="email" />
      </div>

      {/* Honeypot — visually hidden; bots fill it, humans don't. */}
      <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
        <label htmlFor="v-website">Website</label>
        <input id="v-website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>

      <div style={{ ...row, borderTop: '1px solid #f1f1f1', paddingTop: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0B132B', marginBottom: 4 }}>Where would you like to help?</div>
        <p style={{ fontSize: 12.5, color: '#94a3b8', margin: '0 0 12px' }}>
          Pick a state, and narrow to a district or city if you can. You can stop at any level.
        </p>

        <div style={{ marginBottom: 12 }}>
          <label style={label} htmlFor="v-state">State</label>
          <select id="v-state" style={field} value={state} onChange={(e) => setState(e.target.value)} required>
            <option value="">Choose a state…</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={label} htmlFor="v-district">District <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span></label>
          <select id="v-district" style={{ ...field, opacity: state ? 1 : 0.5 }} value={district} onChange={(e) => setDistrict(e.target.value)} disabled={!state}>
            <option value="">{state ? (districts.length ? 'Choose a district…' : 'No districts found') : 'Choose a state first'}</option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div>
          <label style={label} htmlFor="v-city">City <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span></label>
          <select id="v-city" style={{ ...field, opacity: district ? 1 : 0.5 }} value={city} onChange={(e) => setCity(e.target.value)} disabled={!district}>
            <option value="">{district ? (cities.length ? 'Choose a city…' : 'No cities found') : 'Choose a district first'}</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div style={{ ...row, marginTop: 4 }}>
        <label style={label} htmlFor="v-motivation">Why do you want to help? <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span></label>
        <textarea id="v-motivation" style={{ ...field, minHeight: 96, resize: 'vertical' }} value={motivation} onChange={(e) => setMotivation(e.target.value)} placeholder="A sentence or two about what you'd like to do." />
      </div>

      <button type="submit" disabled={submitting} style={{
        width: '100%', padding: '13px 18px', borderRadius: 12, border: 'none',
        background: submitting ? '#94a3b8' : '#0E7C61', color: 'white', fontSize: 15, fontWeight: 800,
        fontFamily: 'inherit', cursor: submitting ? 'default' : 'pointer', marginTop: 6,
      }}>
        {submitting ? 'Submitting…' : 'Submit application'}
      </button>
    </form>
  )
}
